import React, { useRef, useEffect, useState } from 'react'
import { View } from 'react-native'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { setDomAttributes } from '../../../utils/setDomAttributes'
import { useTaskHierarchy, taskHierarchyStyles } from '../TaskHierarchy'

import DismissibleItem from '../../UIComponents/DismissibleItem'
import TaskPresentation from '../TaskItem/TaskPresentation/TaskPresentation'
import { setCheckTaskItem } from '../../../redux/actions'
import ParentTaskContainer from '../../TaskListView/ParentTaskContainer'
import SharedHelper from '../../../utils/SharedHelper'
import AddTask from '../AddTask'
import store from '../../../redux/store'
import ProjectHelper from '../../SettingsView/ProjectsSettings/ProjectHelper'
import EditTask from '../TaskItem/EditTask'
import { sortTasksByPriority } from '../../../utils/TaskPriority'
import { useIsUserEditing } from '../../../utils/editingGuard'
import useProjectPostponePreview from '../../../hooks/useProjectPostponePreview'
import { projectTaskPreview } from '../../../utils/backends/Tasks/optimisticProjectPostpone'

export default function SubTasksView({
    projectId,
    hideSubtaskList,
    showSubtaskList,
    parentTask,
    subtaskList,
    isActiveOrganizeMode,
    isObservedTask,
    isToReviewTask,
    isPending,
    createSubtaskRequest,
}) {
    const taskHierarchy = useTaskHierarchy()
    const dispatch = useDispatch()
    const checkTaskItem = useSelector(state => state.checkTaskItem)
    const focusedTaskItem = useSelector(state => state.focusedTaskItem)
    const focusedTaskId = useSelector(state => state.loggedUser.inFocusTaskId)
    const loggedUserId = useSelector(state => state.loggedUser.uid)
    const isAnonymous = useSelector(state => state.loggedUser.isAnonymous)
    const projectIds = useSelector(state => state.loggedUser.projectIds, shallowEqual)
    const [firstLoadedEnded, setFirstLoadedEnded] = useState(false)
    const isUserEditing = useIsUserEditing()

    const dismissibleRefs = useRef([])
    const taskItemRefs = useRef([])
    const newItemRef = useRef(null)
    const parentRefsList = useRef([])

    const parentInTaskOutOfOpen = isPending || isToReviewTask || parentTask.inDone
    const postponeEntry = useProjectPostponePreview(projectId)
    const displayedSubtasks =
        !postponeEntry || parentInTaskOutOfOpen || isObservedTask
            ? subtaskList
            : subtaskList.flatMap(task => {
                  const preview = projectTaskPreview(task, postponeEntry)
                  return preview.hidden ? [] : [preview.task]
              })
    const renderedSubtaskList = parentInTaskOutOfOpen
        ? displayedSubtasks
        : sortTasksByPriority(displayedSubtasks, focusedTaskId)

    const setAriaTaskId = () => {
        for (let index in renderedSubtaskList) {
            const task = renderedSubtaskList[index]
            if (task && parentRefsList.current[index]) {
                setDomAttributes(parentRefsList.current[index], { 'aria-task-id': task.id })
                setDomAttributes(parentRefsList.current[index], {
                    'is-observed-task': isObservedTask ? 'true' : 'false',
                })
            }
        }
    }

    const openTaskModal = index => {
        dismissibleRefs.current[index].openModal()
    }

    const editModeCheckOff = index => {
        dismissibleRefs.current[index].toggleModal()
        setTimeout(() => {
            taskItemRefs.current[index].onCheckboxPress()
        }, 100)
    }

    const findTaskIndexById = taskId => {
        return renderedSubtaskList.findIndex(subT => subT.id === taskId)
    }

    useEffect(() => {
        if (subtaskList.length === 0 && !createSubtaskRequest) newItemRef.current.toggleModal()
        setFirstLoadedEnded(true)
    }, [])

    useEffect(() => {
        if (createSubtaskRequest > 0) newItemRef.current?.openModal()
    }, [createSubtaskRequest])

    useEffect(() => {
        // `subtaskByTaskStore` is rewritten on every snapshot and can drop to 0
        // transiently. Collapsing here would unmount the open "add subtask"
        // editor together with its typed text, so hold off while the user is
        // busy - `isUserEditing` is in the deps, so it collapses once they stop.
        if (firstLoadedEnded && subtaskList.length === 0 && !isUserEditing) hideSubtaskList()
        setAriaTaskId()
    }, [subtaskList.length, isUserEditing])

    useEffect(() => {
        if (focusedTaskItem.isObserved === !!isObservedTask) {
            const index = findTaskIndexById(focusedTaskItem.id)
            const { activeEditMode } = store.getState()
            if (index > -1 && !activeEditMode) {
                openTaskModal(index)
            }
        }
    }, [focusedTaskItem.id, focusedTaskItem.isObserved])

    useEffect(() => {
        if (checkTaskItem.isObserved === !!isObservedTask) {
            const index = findTaskIndexById(checkTaskItem.id)
            if (index > -1) {
                dispatch(setCheckTaskItem('', false))
                const { activeEditMode } = store.getState()
                if (loggedUserIsParentTaskOwner)
                    activeEditMode ? editModeCheckOff(index) : taskItemRefs.current[index].onCheckboxPress()
            }
        }
    }, [checkTaskItem.id, checkTaskItem.isObserved])

    const accessGranted = SharedHelper.checkIfUserHasAccessToProject(isAnonymous, projectIds, projectId, false)

    const loggedUserIsParentTaskOwner = loggedUserId === parentTask.userId
    const loggedUserCanUpdateObject =
        loggedUserIsParentTaskOwner || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(projectId)

    return (
        <View style={taskHierarchy ? taskHierarchyStyles.subtasks : { marginLeft: 34 }}>
            {renderedSubtaskList.length > 0
                ? isActiveOrganizeMode
                    ? renderedSubtaskList.map(subTask => {
                          return (
                              <ParentTaskContainer
                                  key={subTask.id}
                                  task={subTask}
                                  projectId={projectId}
                                  isActiveOrganizeMode
                                  subtaskList={[]}
                                  isObservedTask={isObservedTask}
                                  isToReviewTask={isToReviewTask}
                              />
                          )
                      })
                    : renderedSubtaskList.map((subTask, index) => {
                          return (
                              <View key={subTask.id} ref={ref => (parentRefsList.current[index] = ref)}>
                                  <DismissibleItem
                                      ref={ref => (dismissibleRefs.current[index] = ref)}
                                      defaultComponent={
                                          <TaskPresentation
                                              ref={ref => (taskItemRefs.current[index] = ref)}
                                              projectId={projectId}
                                              parentTask={parentTask}
                                              task={subTask}
                                              toggleModal={() => {
                                                  dismissibleRefs.current[index]?.toggleModal()
                                              }}
                                              isPending={isPending}
                                              isToReviewTask={isToReviewTask}
                                              isObservedTask={isObservedTask}
                                          />
                                      }
                                      modalComponent={
                                          <EditTask
                                              isSubtask={true}
                                              parentTask={parentTask}
                                              task={subTask}
                                              projectId={projectId}
                                              onCancelAction={forceAction => {
                                                  dismissibleRefs.current[index]?.toggleModal(forceAction)
                                              }}
                                              editModeCheckOff={() => editModeCheckOff(index)}
                                              isObservedTask={isObservedTask}
                                              isToReviewTask={isToReviewTask}
                                              isPending={isPending}
                                          />
                                      }
                                  />
                              </View>
                          )
                      })
                : null}

            {!isActiveOrganizeMode && accessGranted && loggedUserCanUpdateObject && (
                <DismissibleItem
                    ref={newItemRef}
                    defaultComponent={
                        <AddTask
                            projectId={projectId}
                            isSubtask={true}
                            parentTask={parentTask}
                            newItem
                            toggleModal={() => {
                                newItemRef.current.toggleModal()
                            }}
                        />
                    }
                    modalComponent={
                        <EditTask
                            adding={true}
                            isSubtask={true}
                            subtaskList={subtaskList ? subtaskList : []}
                            parentTask={parentTask}
                            projectId={projectId}
                            onCancelAction={forceAction => {
                                newItemRef.current.toggleModal(forceAction)
                            }}
                            isObservedTask={isObservedTask}
                            isToReviewTask={isToReviewTask}
                            defaultDate={parentTask.dueDate}
                            parentInTaskOutOfOpen={parentInTaskOutOfOpen}
                        />
                    }
                    onToggleModal={
                        subtaskList.length === 0
                            ? value => {
                                  value ? showSubtaskList() : hideSubtaskList()
                              }
                            : undefined
                    }
                />
            )}
        </View>
    )
}
